import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ProjectItemStyles = styled.div`
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition: var(--transition-normal);
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-glow);
    border-color: var(--accent-primary);
  }

  .projectItem__img {
    width: 100%;
    height: 280px;
    overflow: hidden;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        rgba(0, 0, 0, 0.4) 100%
      );
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: var(--transition-slow);
    }

    &:hover img {
      transform: scale(1.1);
    }
  }

  .projectItem__info {
    padding: 2.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .projectItem__category {
    display: inline-block;
    padding: 0.5rem 1.2rem;
    background: var(--gradient-primary);
    color: var(--text-primary);
    border-radius: var(--radius-sm);
    font-size: 1.2rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1.5rem;
    width: fit-content;
  }

  .projectItem__title {
    font-size: 2.2rem;
    font-weight: 600;
    margin-bottom: 1.2rem;
    color: var(--text-primary);
    transition: var(--transition-normal);

    &:hover {
      color: var(--accent-primary);
    }
  }

  .projectItem__desc {
    font-size: 1.5rem;
    line-height: 1.7;
    color: var(--text-secondary);
    margin-bottom: 2rem;
    font-weight: 300;
    flex: 1;
  }

  .projectItem__tech {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin-bottom: 2rem;

    .tech-badge {
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid var(--glass-border);
      border-radius: var(--radius-sm);
      font-size: 1.2rem;
      color: var(--text-secondary);
      font-weight: 500;
    }
  }

  .projectButton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1.2rem 2.5rem;
    font-size: 1.5rem;
    font-weight: 600;
    background: var(--gradient-primary);
    color: var(--text-primary);
    border-radius: var(--radius-md);
    text-decoration: none;
    transition: var(--transition-normal);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-glow);
    }
  }

  @media only screen and (max-width: 768px) {
    .projectItem__img {
      height: 220px;
    }

    .projectItem__info {
      padding: 2rem;
    }

    .projectItem__title {
      font-size: 2rem;
    }

    .projectItem__desc {
      font-size: 1.4rem;
    }
  }
`;

export default function ProjectItem({
  img,
  title = "Project Name",
  description = "This is your Projects description",
  link,
  category,
  tech = [],
}) {
  const renderDescription = (text) => {
    const parts = String(text).split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <ProjectItemStyles>
      <Link to="/projects" className="projectItem__img">
        <img src={img} alt={title} />
      </Link>
      <div className="projectItem__info">
        {category && <span className="projectItem__category">{category}</span>}
        <Link to="/projects">
          <h3 className="projectItem__title">{title}</h3>
        </Link>
        <p className="projectItem__desc">{renderDescription(description)}</p>

        {tech.length > 0 && (
          <div className="projectItem__tech">
            {tech.map((techItem, index) => (
              <span key={index} className="tech-badge">
                {techItem}
              </span>
            ))}
          </div>
        )}

        {link && (
          <a
            className="projectButton"
            href={link}
            target="_blank"
            rel="noreferrer"
          >
            View Live Demo
          </a>
        )}
      </div>
    </ProjectItemStyles>
  );
}
